import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveRadar } from "@nivo/radar";
import { NA100_metrics, FormattedNA100Metric } from "./NA100Constants";
import { PublicMetric } from "../../../models/CompanyprofileModels";
import { buildNA100Data } from "./util";
import {
  compassBordeaux,
  compassLightGreen,
  compassYellow,
  lightGreyText,
} from "../../../styles";

enum RadarAreaKey {
  NA100 = "NA100",
}

enum ChartIndexBy {
  Indicator = "indicator",
}

enum IndicatorValueScore {
  Yes = 2,
  Partial = 1,
  No = 0,
  NotRelevant = 0,
  NotAvailable = 0,
  "-" = 0,
}

export enum ScoreTier {
  Bad = "Bad",
  Mid = "Mid",
  Good = "Good",
}

type NivoRadarDatum = Record<string, unknown>;

interface RadarDatum extends NivoRadarDatum {
  indicator: string;
  [RadarAreaKey.NA100]: number;
}

export const TierColor: Record<ScoreTier, string> = {
  [ScoreTier.Bad]: compassBordeaux,
  [ScoreTier.Mid]: compassYellow,
  [ScoreTier.Good]: compassLightGreen,
};

export const getScoreTier = (score: number): ScoreTier => {
  if (score <= 1) return ScoreTier.Bad;
  if (score <= 3) return ScoreTier.Mid;
  return ScoreTier.Good;
};

export const getScoreColor = (score: number): string =>
  TierColor[getScoreTier(score)];

const transformToRadarData = (
  metricsData: PublicMetric[],
  area: RadarAreaKey = RadarAreaKey.NA100
): RadarDatum[] => {
  const indicators: FormattedNA100Metric<NA100_metrics>[] =
    buildNA100Data(metricsData);
  return indicators.map((indicator) => ({
    indicator: indicator.metricName,
    [area]:
      IndicatorValueScore[
        indicator.value as keyof typeof IndicatorValueScore
      ] || IndicatorValueScore.No,
  }));
};

export const NA100RadarChart = ({
  metricsData,
}: {
  metricsData: PublicMetric[];
}): JSX.Element => {
  const [data, setData] = useState<RadarDatum[]>([]);
  const totalScore = useMemo(() => {
    return data.reduce(
      (acc, datum) => acc + (datum[RadarAreaKey.NA100] as number),
      0
    );
  }, [data]);

  const chartColor = useMemo(() => getScoreColor(totalScore), [totalScore]);

  useEffect(() => {
    setData(transformToRadarData(metricsData));
  }, [metricsData]);

  return (
    <ResponsiveRadar<RadarDatum>
      data={data}
      keys={[RadarAreaKey.NA100]}
      indexBy={ChartIndexBy.Indicator}
      maxValue={2}
      gridLevels={2}
      valueFormat=">-.2f"
      margin={{ top: 15, right: 124, bottom: 15, left: 10 }}
      borderColor={chartColor}
      gridLabelOffset={8}
      enableDots={false}
      dotColor={{ theme: "background" }}
      dotBorderWidth={2}
      colors={[chartColor]}
      blendMode="multiply"
      motionConfig="wobbly"
      sliceTooltip={(): null => null}
      legends={[]}
      theme={{
        axis: {
          ticks: {
            text: {
              fontSize: 12,
              fill: lightGreyText,
              fontFamily: "Apercu, Inter, sans-serif",
            },
          },
        },
      }}
    />
  );
};
